import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, Users, Package, MapPin, MessageSquare, 
  Mail, Star, Handshake, Plus, Edit, Trash2, Save, X, 
  TrendingUp, Clock, Eye, Search, ChevronLeft, ChevronRight,
  RefreshCw, CheckCircle, XCircle, AlertCircle, UserCog, Settings,
  CreditCard, Link2, Unlink, Globe, Loader2, ExternalLink, Leaf, Flame,
  Upload, FileText, PlayCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import MemberManagement from '../components/MemberManagement';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  
  // Content states
  const [packages, setPackages] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [quickbooksStatus, setQuickbooksStatus] = useState({ connected: false });
  const [refundPreview, setRefundPreview] = useState(null);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [processingRefund, setProcessingRefund] = useState(false);
  const [ingesterUrl, setIngesterUrl] = useState('');
  const [ingesterTextContent, setIngesterTextContent] = useState('');
  const [ingesterInputMode, setIngesterInputMode] = useState('url'); // 'url', 'text', or 'batch'
  const [ingesterLoading, setIngesterLoading] = useState(false);
  const [ingesterResult, setIngesterResult] = useState(null);
  const [ingestedTrips, setIngestedTrips] = useState([]);
  const [batchUrls, setBatchUrls] = useState('');
  const [batchJob, setBatchJob] = useState(null);
  const [batchJobs, setBatchJobs] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Edit dialog
  const [editingItem, setEditingItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadAnalytics();
    loadAllData();
  }, [user]);

  const loadAnalytics = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/analytics`, { withCredentials: true });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [packagesRes, destinationsRes, testimonialsRes, subscribersRes, usersRes, consultationsRes, partnersRes, qbStatusRes, bookingsRes, ingestedRes] = await Promise.all([
        axios.get(`${API_URL}/packages`, { withCredentials: true }),
        axios.get(`${API_URL}/destinations`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/testimonials`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/subscribers`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/consultations`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/partners`, { withCredentials: true }).catch(() => ({ data: { partners: [] } })),
        axios.get(`${API_URL}/quickbooks/status`, { withCredentials: true }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API_URL}/admin/bookings`, { withCredentials: true }).catch(() => ({ data: { bookings: [] } })),
        axios.get(`${API_URL}/ingester/trips`, { withCredentials: true }).catch(() => ({ data: { trips: [] } }))
      ]);
      
      setPackages(packagesRes.data.packages || []);
      setDestinations(destinationsRes.data.destinations || []);
      setTestimonials(testimonialsRes.data.testimonials || []);
      setSubscribers(subscribersRes.data.subscribers || []);
      setUsers(usersRes.data.users || []);
      setConsultations(consultationsRes.data.consultations || []);
      setPartners(partnersRes.data.partners || []);
      setQuickbooksStatus(qbStatusRes.data || { connected: false });
      setBookings(bookingsRes.data.bookings || []);
      setIngestedTrips(ingestedRes.data.trips || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item, type) => {
    setEditingItem({ ...item });
    setDialogType(type);
    setIsDialogOpen(true);
  };

  const handleCreate = (type) => {
    const templates = {
      package: { name: '', image: '', description: '', duration: '', spots: 10, price: 0, location: '', category: 'Adventure', highlights: [], groupSize: '4-12 people', included: [], sustainable: true, featured: false },
      destination: { name: '', image: '', description: '', discount: '20% OFF', validUntil: 'December 31, 2026', location: '', category: 'Beach & Nightlife' },
      testimonial: { name: '', review: '', rating: 5, trip: '', image: '', featured: false },
      partner: { name: '', logo: '', description: '', affiliateLink: '', category: 'Booking Platform', active: true, display_order: 0 }
    };
    setEditingItem(templates[type] || {});
    setDialogType(type);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      let endpoint = '';
      let method = editingItem.id || editingItem.partner_id ? 'put' : 'post';
      
      switch (dialogType) {
        case 'package':
          endpoint = editingItem.id ? `/admin/packages/${editingItem.id}` : '/packages';
          break;
        case 'destination':
          endpoint = editingItem.id ? `/admin/destinations/${editingItem.id}` : '/destinations';
          break;
        case 'testimonial':
          endpoint = editingItem.id ? `/admin/testimonials/${editingItem.id}` : '/testimonials';
          break;
        case 'partner':
          endpoint = editingItem.partner_id ? `/admin/partners/${editingItem.partner_id}` : '/admin/partners';
          break;
        case 'consultation':
          endpoint = `/admin/consultations/${editingItem._id}`;
          method = 'put';
          break;
        case 'user':
          endpoint = `/admin/users/${editingItem.user_id}`;
          method = 'put';
          break;
        default:
          throw new Error('Unknown type');
      }

      await axios[method](`${API_URL}${endpoint}`, editingItem, { withCredentials: true });
      
      toast.success(`${dialogType} ${method === 'post' ? 'created' : 'updated'} successfully!`);
      setIsDialogOpen(false);
      loadAllData();
      loadAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;
    
    try {
      let endpoint = '';
      switch (type) {
        case 'package':
          endpoint = `/admin/packages/${id}`;
          break;
        case 'destination':
          endpoint = `/admin/destinations/${id}`;
          break;
        case 'testimonial':
          endpoint = `/admin/testimonials/${id}`;
          break;
        case 'partner':
          endpoint = `/admin/partners/${id}`;
          break;
        case 'user':
          endpoint = `/admin/users/${id}`;
          break;
        default:
          throw new Error('Unknown type');
      }
      
      await axios.delete(`${API_URL}${endpoint}`, { withCredentials: true });
      toast.success(`${type} deleted successfully!`);
      loadAllData();
      loadAnalytics();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const updateConsultationStatus = async (consultationId, status) => {
    try {
      await axios.put(`${API_URL}/admin/consultations/${consultationId}`, { status }, { withCredentials: true });
      toast.success('Status updated!');
      loadAllData();
      loadAnalytics();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#84bc00] mx-auto mb-4"></div>
          <p className="text-gray-600 font-semibold">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, trend, color = "text-[#84bc00]" }) => (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-3xl font-black text-gray-900">{value}</p>
            {trend && (
              <p className="text-xs text-green-600 flex items-center mt-1">
                <TrendingUp size={12} className="mr-1" />
                {trend}
              </p>
            )}
          </div>
          <div className={`p-4 rounded-full bg-gray-100 ${color}`}>
            <Icon size={24} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-12 pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-black mb-2">ADMIN DASHBOARD</h1>
              <p className="text-gray-400">Manage your Exciting.Travel content</p>
            </div>
            <Button onClick={loadAllData} variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
              <RefreshCw size={18} className="mr-2" />
              Refresh Data
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-10 mb-8 bg-white shadow-sm">
            <TabsTrigger value="overview" className="font-bold" data-testid="tab-overview">
              <LayoutDashboard size={16} className="mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="bookings" className="font-bold" data-testid="tab-bookings">
              <CreditCard size={16} className="mr-2" />Bookings
            </TabsTrigger>
            <TabsTrigger value="packages" className="font-bold" data-testid="tab-packages">
              <Package size={16} className="mr-2" />Packages
            </TabsTrigger>
            <TabsTrigger value="destinations" className="font-bold" data-testid="tab-destinations">
              <MapPin size={16} className="mr-2" />Destinations
            </TabsTrigger>
            <TabsTrigger value="consultations" className="font-bold" data-testid="tab-consultations">
              <MessageSquare size={16} className="mr-2" />Consults
            </TabsTrigger>
            <TabsTrigger value="users" className="font-bold" data-testid="tab-users">
              <Users size={16} className="mr-2" />Users
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="font-bold" data-testid="tab-testimonials">
              <Star size={16} className="mr-2" />Reviews
            </TabsTrigger>
            <TabsTrigger value="ingester" className="font-bold bg-purple-600 text-white data-[state=active]:bg-purple-700" data-testid="tab-ingester">
              <Globe size={16} className="mr-2" />Ingester
            </TabsTrigger>
            <TabsTrigger value="members" className="font-bold" data-testid="tab-members">
              <UserCog size={16} className="mr-2" />CRM
            </TabsTrigger>
            <TabsTrigger value="settings" className="font-bold" data-testid="tab-settings">
              <Settings size={16} className="mr-2" />Settings
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            <div className="space-y-8">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                  title="Total Users" 
                  value={analytics?.overview?.total_users || 0} 
                  icon={Users}
                  trend={analytics?.recent_activity?.new_users_7d > 0 ? `+${analytics?.recent_activity?.new_users_7d} this week` : null}
                />
                <StatCard 
                  title="Travel Packages" 
                  value={analytics?.overview?.total_packages || 0} 
                  icon={Package}
                  color="text-[#ffb84d]"
                />
                <StatCard 
                  title="Destinations" 
                  value={analytics?.overview?.total_destinations || 0} 
                  icon={MapPin}
                  color="text-blue-500"
                />
                <StatCard 
                  title="Newsletter Subscribers" 
                  value={analytics?.overview?.total_subscribers || 0} 
                  icon={Mail}
                  trend={analytics?.recent_activity?.new_subscribers_7d > 0 ? `+${analytics?.recent_activity?.new_subscribers_7d} this week` : null}
                  color="text-purple-500"
                />
              </div>

              {/* Secondary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border-l-4 border-l-yellow-500">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Clock className="mr-2 text-yellow-500" size={20} />
                      Pending Consultations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-black text-yellow-600">{analytics?.overview?.pending_consultations || 0}</p>
                    <p className="text-sm text-gray-500 mt-1">Awaiting response</p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <MessageSquare className="mr-2 text-green-500" size={20} />
                      Total Consultations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-black text-green-600">{analytics?.overview?.total_consultations || 0}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      +{analytics?.recent_activity?.new_consultations_7d || 0} this week
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-pink-500">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Star className="mr-2 text-pink-500" size={20} />
                      Testimonials
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-black text-pink-600">{analytics?.overview?.total_testimonials || 0}</p>
                    <p className="text-sm text-gray-500 mt-1">Customer reviews</p>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    <Button onClick={() => handleCreate('package')} className="bg-[#84bc00] hover:bg-[#6a9600]">
                      <Plus size={18} className="mr-2" />Add Package
                    </Button>
                    <Button onClick={() => handleCreate('destination')} className="bg-[#ffb84d] hover:bg-[#e5a643] text-black">
                      <Plus size={18} className="mr-2" />Add Destination
                    </Button>
                    <Button onClick={() => handleCreate('partner')} variant="outline">
                      <Plus size={18} className="mr-2" />Add Partner
                    </Button>
                    <Button onClick={() => setActiveTab('consultations')} variant="outline">
                      <Eye size={18} className="mr-2" />View Consultations
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* PACKAGES TAB */}
          <TabsContent value="packages">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Travel Packages</CardTitle>
                    <CardDescription>Manage your travel package offerings</CardDescription>
                  </div>
                  <Button onClick={() => handleCreate('package')} className="bg-[#84bc00] hover:bg-[#6a9600]">
                    <Plus size={18} className="mr-2" />ADD PACKAGE
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {packages.map((pkg) => (
                    <div key={pkg.id} className="flex items-center border-2 rounded-lg p-4 hover:border-[#84bc00] transition-colors" data-testid={`package-item-${pkg.id}`}>
                      <img src={pkg.image} alt={pkg.name} className="w-20 h-20 object-cover rounded-lg mr-4" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-black">{pkg.name}</h3>
                          {pkg.featured && <Badge className="bg-[#ffb84d] text-black">Featured</Badge>}
                          {pkg.sustainable && <Badge className="bg-[#84bc00] text-black">Eco</Badge>}
                        </div>
                        <p className="text-sm text-gray-600">{pkg.location} • {pkg.duration} • ${pkg.price}</p>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-1">{pkg.description}</p>
                      </div>
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(pkg, 'package')} data-testid={`edit-package-${pkg.id}`}>
                          <Edit size={16} />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(pkg.id, 'package')} data-testid={`delete-package-${pkg.id}`}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {packages.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No packages found. Create your first package!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DESTINATIONS TAB */}
          <TabsContent value="destinations">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Destinations</CardTitle>
                    <CardDescription>Manage travel destinations</CardDescription>
                  </div>
                  <Button onClick={() => handleCreate('destination')} className="bg-[#84bc00] hover:bg-[#6a9600]">
                    <Plus size={18} className="mr-2" />ADD DESTINATION
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {destinations.map((dest) => (
                    <div key={dest.id} className="border-2 rounded-lg p-4 hover:border-[#84bc00] transition-colors" data-testid={`destination-item-${dest.id}`}>
                      <div className="flex items-start gap-4">
                        <img src={dest.image} alt={dest.name} className="w-24 h-24 object-cover rounded-lg" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-black">{dest.name}</h3>
                            <Badge className="bg-[#ffb84d] text-black">{dest.discount}</Badge>
                          </div>
                          <p className="text-sm text-gray-600">{dest.location} • {dest.category}</p>
                          <p className="text-xs text-gray-500 mt-1">Valid until: {dest.validUntil}</p>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2 mt-4">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(dest, 'destination')}>
                          <Edit size={16} className="mr-1" />Edit
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(dest.id, 'destination')}>
                          <Trash2 size={16} className="mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CONSULTATIONS TAB */}
          <TabsContent value="consultations">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageSquare className="mr-2 text-[#84bc00]" />
                  Consultation Requests
                </CardTitle>
                <CardDescription>Manage incoming consultation requests from potential customers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {consultations.map((consult) => (
                    <div key={consult._id} className="border-2 rounded-lg p-4 hover:border-[#84bc00] transition-colors" data-testid={`consultation-item-${consult._id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-black">{consult.name}</h3>
                            <Badge variant={
                              consult.status === 'pending' ? 'outline' :
                              consult.status === 'contacted' ? 'secondary' :
                              consult.status === 'booked' ? 'default' : 'destructive'
                            } className={
                              consult.status === 'pending' ? 'border-yellow-500 text-yellow-600' :
                              consult.status === 'booked' ? 'bg-green-500' : ''
                            }>
                              {consult.status?.toUpperCase()}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                            <p><strong>Email:</strong> {consult.email}</p>
                            <p><strong>Phone:</strong> {consult.phone || 'N/A'}</p>
                            <p><strong>Destination:</strong> {consult.destination}</p>
                            <p><strong>Travelers:</strong> {consult.travelers}</p>
                            <p><strong>Dates:</strong> {consult.dates || 'Flexible'}</p>
                            <p><strong>Date:</strong> {new Date(consult.createdAt).toLocaleDateString()}</p>
                          </div>
                          {consult.message && (
                            <p className="mt-2 text-sm text-gray-500 bg-gray-50 p-2 rounded">
                              <strong>Message:</strong> {consult.message}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <Select value={consult.status} onValueChange={(value) => updateConsultationStatus(consult._id, value)}>
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="booked">Booked</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                  {consultations.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No consultation requests yet.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <Users className="mr-2 text-[#84bc00]" />
                      User Management
                    </CardTitle>
                    <CardDescription>View and manage registered users</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-64"
                    />
                    <Search className="text-gray-400" size={20} />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.filter(u => 
                    !searchTerm || 
                    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
                  ).map((user) => (
                    <div key={user.user_id} className="flex items-center border-2 rounded-lg p-4 hover:border-[#84bc00] transition-colors" data-testid={`user-item-${user.user_id}`}>
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mr-4">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <span className="text-xl font-bold text-gray-500">{user.name?.[0]?.toUpperCase() || 'U'}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold">{user.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            {user.auth_method === 'google' ? '🔵 Google' : '📧 Email'}
                          </Badge>
                          {user.role === 'admin' ? (
                            <Badge className="bg-[#ffb84d] text-black text-xs">ADMIN</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">User</Badge>
                          )}
                          {user.email_verified ? (
                            <Badge className="bg-green-100 text-green-700 text-xs">Verified</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 text-xs">Unverified</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        <p className="text-xs text-gray-400">Joined: {new Date(user.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(user, 'user')}>
                          <Edit size={16} />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(user.user_id, 'user')}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {users.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No users registered yet.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TESTIMONIALS TAB */}
          <TabsContent value="testimonials">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Customer Testimonials</CardTitle>
                    <CardDescription>Manage customer reviews and testimonials</CardDescription>
                  </div>
                  <Button onClick={() => handleCreate('testimonial')} className="bg-[#84bc00] hover:bg-[#6a9600]">
                    <Plus size={18} className="mr-2" />ADD TESTIMONIAL
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {testimonials.map((test) => (
                    <div key={test.id} className="border-2 rounded-lg p-4 hover:border-[#84bc00] transition-colors" data-testid={`testimonial-item-${test.id}`}>
                      <div className="flex items-start gap-4">
                        <img src={test.image} alt={test.name} className="w-16 h-16 rounded-full object-cover" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold">{test.name}</h3>
                            {test.featured && <Badge className="bg-[#ffb84d] text-black">Featured</Badge>}
                            <div className="flex">
                              {[...Array(test.rating)].map((_, i) => (
                                <Star key={i} size={14} className="text-yellow-500 fill-yellow-500" />
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">{test.trip}</p>
                          <p className="text-sm text-gray-500 italic">"{test.review}"</p>
                        </div>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(test, 'testimonial')}>
                            <Edit size={16} />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(test.id, 'testimonial')}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {testimonials.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No testimonials yet. Add your first testimonial!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PARTNERS TAB */}
          <TabsContent value="partners">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Partner Management</CardTitle>
                    <CardDescription>Manage affiliate partners and links</CardDescription>
                  </div>
                  <Button onClick={() => handleCreate('partner')} className="bg-[#84bc00] hover:bg-[#6a9600]">
                    <Plus size={18} className="mr-2" />ADD PARTNER
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {partners.map((partner) => (
                    <div key={partner.partner_id} className="border-2 rounded-lg p-4 hover:border-[#84bc00] transition-colors" data-testid={`partner-item-${partner.partner_id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center p-2">
                          <img src={partner.logo} alt={partner.name} className="max-w-full max-h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold">{partner.name}</h3>
                            <Badge variant={partner.active ? "default" : "secondary"} className={partner.active ? "bg-green-500" : ""}>
                              {partner.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">{partner.category}</p>
                          <p className="text-xs text-gray-500 mt-1 truncate">{partner.affiliateLink}</p>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2 mt-4">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(partner, 'partner')}>
                          <Edit size={16} className="mr-1" />Edit
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(partner.partner_id, 'partner')}>
                          <Trash2 size={16} className="mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                  {partners.length === 0 && (
                    <p className="text-center text-gray-500 py-8 col-span-2">No partners yet. Add your first partner!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PARTNER INGESTER TAB */}
          <TabsContent value="ingester">
            <div className="space-y-6">
              {/* Ingester Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl font-black flex items-center gap-2">
                    <Globe className="text-purple-600" />
                    Partner Trip Ingester
                  </CardTitle>
                  <CardDescription>
                    Import trips from any partner site - AI extracts details, creates payment links, and syncs to Webflow
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Input Mode Tabs */}
                  <div className="flex gap-2 mb-4">
                    <Button
                      variant={ingesterInputMode === 'url' ? 'default' : 'outline'}
                      onClick={() => setIngesterInputMode('url')}
                      className={ingesterInputMode === 'url' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                      data-testid="ingester-mode-url"
                    >
                      <Link2 size={16} className="mr-2" />
                      Single URL
                    </Button>
                    <Button
                      variant={ingesterInputMode === 'text' ? 'default' : 'outline'}
                      onClick={() => setIngesterInputMode('text')}
                      className={ingesterInputMode === 'text' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                      data-testid="ingester-mode-text"
                    >
                      <Edit size={16} className="mr-2" />
                      Paste Content
                    </Button>
                    <Button
                      variant={ingesterInputMode === 'batch' ? 'default' : 'outline'}
                      onClick={() => {
                        setIngesterInputMode('batch');
                        // Load batch jobs
                        axios.get(`${API_URL}/ingester/batch/jobs`, { withCredentials: true })
                          .then(res => setBatchJobs(res.data.jobs || []))
                          .catch(() => {});
                      }}
                      className={ingesterInputMode === 'batch' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                      data-testid="ingester-mode-batch"
                    >
                      <Upload size={16} className="mr-2" />
                      Batch Import
                    </Button>
                  </div>

                  {/* URL Input Mode */}
                  {ingesterInputMode === 'url' && (
                    <div className="space-y-4">
                      <div className="flex gap-4">
                        <Input
                          placeholder="Paste trip URL (e.g., https://partner-site.com/trips/costa-rica-adventure)"
                          value={ingesterUrl}
                          onChange={(e) => setIngesterUrl(e.target.value)}
                          className="flex-1"
                          data-testid="ingester-url-input"
                        />
                        <Button
                          className="bg-purple-600 hover:bg-purple-700 px-8"
                          onClick={async () => {
                            if (!ingesterUrl.trim()) {
                              toast.error('Please enter a URL');
                              return;
                            }
                            setIngesterLoading(true);
                            setIngesterResult(null);
                            try {
                              const res = await axios.post(`${API_URL}/ingester/ingest`, 
                                { url: ingesterUrl },
                                { withCredentials: true }
                              );
                              setIngesterResult(res.data);
                              setIngesterUrl('');
                              toast.success('Trip ingested successfully!');
                              // Refresh ingested trips list
                              const tripsRes = await axios.get(`${API_URL}/ingester/trips`, { withCredentials: true });
                              setIngestedTrips(tripsRes.data.trips || []);
                            } catch (err) {
                              const errorMsg = err.response?.data?.detail || 'Failed to ingest trip';
                              toast.error(errorMsg);
                              // If URL fetch blocked, suggest text paste mode
                              if (errorMsg.includes('text paste')) {
                                toast.info('Tip: Try "Paste Content" mode for sites with bot protection', { duration: 5000 });
                              }
                            } finally {
                              setIngesterLoading(false);
                            }
                          }}
                          disabled={ingesterLoading || !ingesterUrl.trim()}
                          data-testid="ingester-url-submit-btn"
                        >
                          {ingesterLoading ? (
                            <>
                              <Loader2 size={16} className="mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Globe size={16} className="mr-2" />
                              Fetch & Parse
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-sm text-gray-500">
                        💡 If the site has bot protection, use "Paste Content" mode instead
                      </p>
                    </div>
                  )}

                  {/* Text Paste Mode */}
                  {ingesterInputMode === 'text' && (
                    <div className="space-y-4">
                      <Textarea
                        placeholder="Paste the trip page content here...

Copy the trip details from any website and paste them here. Include:
- Trip name and description
- Price
- Duration (days)
- Itinerary
- What's included
- Destination

AI will intelligently extract all the relevant information."
                        value={ingesterTextContent}
                        onChange={(e) => setIngesterTextContent(e.target.value)}
                        className="min-h-[200px] font-mono text-sm"
                        data-testid="ingester-text-input"
                      />
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-500">
                          {ingesterTextContent.length} characters
                          {ingesterTextContent.length > 0 && ingesterTextContent.length < 100 && (
                            <span className="text-orange-500 ml-2">• Need at least 100 characters</span>
                          )}
                        </p>
                        <Button
                          className="bg-purple-600 hover:bg-purple-700 px-8"
                          onClick={async () => {
                            if (ingesterTextContent.length < 100) {
                              toast.error('Please paste more content (at least 100 characters)');
                              return;
                            }
                            setIngesterLoading(true);
                            setIngesterResult(null);
                            try {
                              const res = await axios.post(`${API_URL}/ingester/ingest`, 
                                { text_content: ingesterTextContent },
                                { withCredentials: true }
                              );
                              setIngesterResult(res.data);
                              setIngesterTextContent('');
                              toast.success('Trip ingested successfully!');
                              // Refresh ingested trips list
                              const tripsRes = await axios.get(`${API_URL}/ingester/trips`, { withCredentials: true });
                              setIngestedTrips(tripsRes.data.trips || []);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to parse content');
                            } finally {
                              setIngesterLoading(false);
                            }
                          }}
                          disabled={ingesterLoading || ingesterTextContent.length < 100}
                          data-testid="ingester-text-submit-btn"
                        >
                          {ingesterLoading ? (
                            <>
                              <Loader2 size={16} className="mr-2 animate-spin" />
                              Parsing...
                            </>
                          ) : (
                            <>
                              <CheckCircle size={16} className="mr-2" />
                              Parse Content
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Batch Import Mode */}
                  {ingesterInputMode === 'batch' && (
                    <div className="space-y-6">
                      {/* Batch URL Input */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-base font-bold">Paste Multiple URLs</Label>
                          <span className="text-sm text-gray-500">One URL per line (max 50)</span>
                        </div>
                        <Textarea
                          placeholder="https://partner-site.com/trip/costa-rica&#10;https://partner-site.com/trip/peru-machu-picchu&#10;https://partner-site.com/trip/colombia-coffee"
                          value={batchUrls}
                          onChange={(e) => setBatchUrls(e.target.value)}
                          className="min-h-[150px] font-mono text-sm"
                          data-testid="batch-urls-input"
                        />
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-500">
                            {batchUrls.split('\n').filter(u => u.trim().startsWith('http')).length} URLs detected
                          </p>
                          <Button
                            className="bg-purple-600 hover:bg-purple-700"
                            onClick={async () => {
                              const urls = batchUrls.split('\n')
                                .map(u => u.trim())
                                .filter(u => u.startsWith('http'));
                              
                              if (urls.length === 0) {
                                toast.error('No valid URLs found');
                                return;
                              }
                              if (urls.length > 50) {
                                toast.error('Maximum 50 URLs per batch');
                                return;
                              }
                              
                              setIngesterLoading(true);
                              try {
                                const res = await axios.post(`${API_URL}/ingester/batch/start`, 
                                  { urls },
                                  { withCredentials: true }
                                );
                                setBatchJob(res.data);
                                setBatchUrls('');
                                toast.success(`Batch import started! Processing ${urls.length} URLs...`);
                                
                                // Start polling for status
                                const pollStatus = setInterval(async () => {
                                  try {
                                    const statusRes = await axios.get(
                                      `${API_URL}/ingester/batch/status/${res.data.job_id}`,
                                      { withCredentials: true }
                                    );
                                    setBatchJob(statusRes.data);
                                    
                                    if (statusRes.data.status === 'completed') {
                                      clearInterval(pollStatus);
                                      toast.success(`Batch import complete! ${statusRes.data.summary?.successful || 0} trips imported.`);
                                      // Refresh trips list
                                      const tripsRes = await axios.get(`${API_URL}/ingester/trips`, { withCredentials: true });
                                      setIngestedTrips(tripsRes.data.trips || []);
                                      // Refresh batch jobs
                                      const jobsRes = await axios.get(`${API_URL}/ingester/batch/jobs`, { withCredentials: true });
                                      setBatchJobs(jobsRes.data.jobs || []);
                                    }
                                  } catch (err) {
                                    console.error('Status poll error:', err);
                                  }
                                }, 3000);
                                
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to start batch import');
                              } finally {
                                setIngesterLoading(false);
                              }
                            }}
                            disabled={ingesterLoading || batchUrls.split('\n').filter(u => u.trim().startsWith('http')).length === 0}
                            data-testid="batch-start-btn"
                          >
                            <PlayCircle size={16} className="mr-2" />
                            Start Batch Import
                          </Button>
                        </div>
                      </div>

                      {/* OR Divider */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-white px-2 text-gray-500">Or upload a file</span>
                        </div>
                      </div>

                      {/* File Upload */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-400 transition-colors">
                        <input
                          type="file"
                          accept=".csv,.txt"
                          className="hidden"
                          id="batch-file-upload"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            setIngesterLoading(true);
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            try {
                              const res = await axios.post(`${API_URL}/ingester/batch/upload`, 
                                formData,
                                { 
                                  withCredentials: true,
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                }
                              );
                              setBatchJob(res.data);
                              toast.success(`File uploaded! Processing ${res.data.total_urls} URLs...`);
                              
                              // Start polling for status
                              const pollStatus = setInterval(async () => {
                                try {
                                  const statusRes = await axios.get(
                                    `${API_URL}/ingester/batch/status/${res.data.job_id}`,
                                    { withCredentials: true }
                                  );
                                  setBatchJob(statusRes.data);
                                  
                                  if (statusRes.data.status === 'completed') {
                                    clearInterval(pollStatus);
                                    toast.success(`Batch import complete! ${statusRes.data.summary?.successful || 0} trips imported.`);
                                    // Refresh trips list
                                    const tripsRes = await axios.get(`${API_URL}/ingester/trips`, { withCredentials: true });
                                    setIngestedTrips(tripsRes.data.trips || []);
                                    // Refresh batch jobs
                                    const jobsRes = await axios.get(`${API_URL}/ingester/batch/jobs`, { withCredentials: true });
                                    setBatchJobs(jobsRes.data.jobs || []);
                                  }
                                } catch (err) {
                                  console.error('Status poll error:', err);
                                }
                              }, 3000);
                              
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to upload file');
                            } finally {
                              setIngesterLoading(false);
                              e.target.value = '';
                            }
                          }}
                          data-testid="batch-file-input"
                        />
                        <label htmlFor="batch-file-upload" className="cursor-pointer">
                          <Upload size={48} className="mx-auto text-gray-400 mb-4" />
                          <p className="font-medium text-gray-700">Click to upload CSV or TXT file</p>
                          <p className="text-sm text-gray-500 mt-1">
                            CSV with 'url' column, or TXT with one URL per line
                          </p>
                        </label>
                      </div>

                      {/* Active Batch Job Progress */}
                      {batchJob && batchJob.status !== 'completed' && (
                        <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
                          <div className="flex items-center gap-2 mb-4">
                            <Loader2 className="text-purple-600 animate-spin" size={24} />
                            <h3 className="text-xl font-bold text-purple-800">Batch Import in Progress</h3>
                          </div>
                          
                          <div className="mb-4">
                            <div className="flex justify-between text-sm mb-1">
                              <span>Progress</span>
                              <span>{batchJob.progress?.current || 0} / {batchJob.progress?.total || 0}</span>
                            </div>
                            <div className="w-full bg-purple-200 rounded-full h-3">
                              <div 
                                className="bg-purple-600 h-3 rounded-full transition-all duration-500"
                                style={{ width: `${batchJob.progress?.percent || 0}%` }}
                              />
                            </div>
                          </div>
                          
                          {batchJob.current_url && (
                            <p className="text-sm text-purple-600 truncate">
                              Processing: {batchJob.current_url}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Completed Batch Job Results */}
                      {batchJob && batchJob.status === 'completed' && (
                        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                          <div className="flex items-center gap-2 mb-4">
                            <CheckCircle className="text-green-600" size={24} />
                            <h3 className="text-xl font-bold text-green-800">Batch Import Complete!</h3>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="bg-white p-4 rounded-lg text-center">
                              <p className="text-2xl font-bold text-gray-700">{batchJob.summary?.total || 0}</p>
                              <p className="text-sm text-gray-500">Total URLs</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg text-center">
                              <p className="text-2xl font-bold text-green-600">{batchJob.summary?.successful || 0}</p>
                              <p className="text-sm text-gray-500">Successful</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg text-center">
                              <p className="text-2xl font-bold text-red-600">{batchJob.summary?.failed || 0}</p>
                              <p className="text-sm text-gray-500">Failed</p>
                            </div>
                          </div>
                          
                          {batchJob.results?.some(r => !r.success) && (
                            <details className="mt-4">
                              <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                                View failed imports ({batchJob.summary?.failed || 0})
                              </summary>
                              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                                {batchJob.results?.filter(r => !r.success).map((r, i) => (
                                  <div key={i} className="text-xs bg-white p-2 rounded border">
                                    <p className="font-mono truncate">{r.url}</p>
                                    <p className="text-red-600">{r.error}</p>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                          
                          <Button 
                            variant="outline" 
                            className="mt-4"
                            onClick={() => setBatchJob(null)}
                          >
                            Clear & Start New
                          </Button>
                        </div>
                      )}

                      {/* Recent Batch Jobs */}
                      {batchJobs.length > 0 && (
                        <div className="mt-6">
                          <h4 className="font-bold text-gray-700 mb-3">Recent Batch Jobs</h4>
                          <div className="space-y-2">
                            {batchJobs.slice(0, 5).map(job => (
                              <div 
                                key={job.job_id} 
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm"
                              >
                                <div className="flex items-center gap-3">
                                  {job.status === 'completed' ? (
                                    <CheckCircle size={16} className="text-green-500" />
                                  ) : job.status === 'processing' ? (
                                    <Loader2 size={16} className="text-purple-500 animate-spin" />
                                  ) : (
                                    <Clock size={16} className="text-gray-400" />
                                  )}
                                  <span className="font-mono text-xs">{job.job_id}</span>
                                  {job.source_file && (
                                    <Badge variant="outline" className="text-xs">
                                      <FileText size={10} className="mr-1" />
                                      {job.source_file}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className="text-gray-500">
                                    {job.summary ? (
                                      <span>
                                        <span className="text-green-600">{job.summary.successful}</span>
                                        {' / '}
                                        <span>{job.total_urls}</span>
                                      </span>
                                    ) : (
                                      <span>{job.progress?.percent || 0}%</span>
                                    )}
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={async () => {
                                      const res = await axios.get(
                                        `${API_URL}/ingester/batch/status/${job.job_id}`,
                                        { withCredentials: true }
                                      );
                                      setBatchJob(res.data);
                                    }}
                                  >
                                    View
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Loading State */}
                  {ingesterLoading && (
                    <div className="mt-6 p-8 bg-purple-50 rounded-lg text-center">
                      <Loader2 size={48} className="mx-auto text-purple-600 animate-spin mb-4" />
                      <p className="font-bold text-purple-900">AI is extracting trip details...</p>
                      <p className="text-sm text-purple-600 mt-2">
                        Parsing content → Extracting fields → Creating Stripe links → Syncing to Webflow
                      </p>
                    </div>
                  )}

                  {/* Result Display */}
                  {ingesterResult && (
                    <div className="mt-6 bg-green-50 border-2 border-green-200 rounded-xl p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <CheckCircle className="text-green-600" size={24} />
                        <h3 className="text-xl font-bold text-green-800">Trip Ingested Successfully!</h3>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-500">Price</p>
                          <p className="text-2xl font-bold text-green-600">
                            ${ingesterResult.price?.toLocaleString() || 'N/A'}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-500">Duration</p>
                          <p className="text-2xl font-bold text-gray-700">
                            {ingesterResult.duration_days || '?'} days
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-500">Burn Level</p>
                          <p className="text-2xl font-bold text-orange-500 flex items-center gap-1">
                            <Flame size={20} />
                            {ingesterResult.parsed_data?.burn_label || 'The Vibe'} ({ingesterResult.burn_rating}/3)
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-500">Vibe</p>
                          <p className="text-2xl font-bold text-purple-600">
                            {ingesterResult.vibe}
                          </p>
                        </div>
                      </div>

                      <div className="bg-white p-4 rounded-lg mb-4">
                        <h4 className="font-bold mb-2">{ingesterResult.title}</h4>
                        {ingesterResult.parsed_data?.destination && (
                          <p className="text-sm text-gray-600 mb-1">📍 {ingesterResult.parsed_data.destination}</p>
                        )}
                        {ingesterResult.parsed_data?.description && (
                          <p className="text-sm text-gray-500 line-clamp-2">{ingesterResult.parsed_data.description}</p>
                        )}
                      </div>

                      {/* Highlights */}
                      {ingesterResult.parsed_data?.highlights?.length > 0 && (
                        <div className="bg-white p-4 rounded-lg mb-4">
                          <p className="text-sm text-gray-500 mb-2 font-medium">✨ Highlights</p>
                          <div className="flex flex-wrap gap-2">
                            {ingesterResult.parsed_data.highlights.slice(0, 5).map((highlight, i) => (
                              <Badge key={i} variant="outline" className="text-gray-700">
                                {highlight}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {ingesterResult.eco_signals?.length > 0 && (
                        <div className="bg-white p-4 rounded-lg mb-4">
                          <p className="text-sm text-gray-500 mb-2 flex items-center gap-1">
                            <Leaf size={14} className="text-green-500" />
                            Eco Signals Detected
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {ingesterResult.eco_signals.map((signal, i) => (
                              <Badge key={i} className="bg-green-100 text-green-700">
                                {signal.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {ingesterResult.stripe_links && Object.keys(ingesterResult.stripe_links).length > 0 && (
                        <div className="bg-white p-4 rounded-lg mb-4">
                          <p className="text-sm text-gray-500 mb-2">Payment Links</p>
                          <div className="flex flex-wrap gap-4">
                            {ingesterResult.stripe_links.full_payment?.url && (
                              <a 
                                href={ingesterResult.stripe_links.full_payment.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-blue-600 hover:underline"
                              >
                                <ExternalLink size={14} />
                                Full Payment (${ingesterResult.stripe_links.full_payment.amount?.toLocaleString()})
                              </a>
                            )}
                            {ingesterResult.stripe_links.deposit?.url && (
                              <a 
                                href={ingesterResult.stripe_links.deposit.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-blue-600 hover:underline"
                              >
                                <ExternalLink size={14} />
                                Deposit (${ingesterResult.stripe_links.deposit.amount?.toLocaleString()})
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      <p className="text-sm text-green-600">
                        ✓ Slack notification sent &nbsp;|&nbsp; 
                        ✓ Added to Packages &nbsp;|&nbsp;
                        {ingesterResult.webflow_item_id ? '✓ Webflow draft created' : '○ Webflow not configured'}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recently Ingested Trips */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package size={20} />
                    Recently Ingested Partner Trips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ingestedTrips.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Globe size={48} className="mx-auto mb-4 opacity-30" />
                      <p>No partner trips ingested yet</p>
                      <p className="text-sm">Paste a URL or trip content above to get started</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-bold">Trip</th>
                            <th className="px-4 py-3 text-left text-sm font-bold">Duration</th>
                            <th className="px-4 py-3 text-left text-sm font-bold">Price</th>
                            <th className="px-4 py-3 text-left text-sm font-bold">Burn Level</th>
                            <th className="px-4 py-3 text-left text-sm font-bold">Vibe</th>
                            <th className="px-4 py-3 text-left text-sm font-bold">Eco</th>
                            <th className="px-4 py-3 text-left text-sm font-bold">Links</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {ingestedTrips.map((trip) => (
                            <tr key={trip.trip_id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="font-medium">{trip.title}</div>
                                {trip.source_url && (
                                  <a href={trip.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                                    <ExternalLink size={10} />
                                    Source
                                  </a>
                                )}
                                {!trip.source_url && (
                                  <span className="text-xs text-gray-400">Pasted content</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">{trip.duration_days || '-'} days</td>
                              <td className="px-4 py-3 text-sm font-bold text-green-600">
                                {trip.price ? `$${trip.price?.toLocaleString()}` : '-'}
                              </td>
                              <td className="px-4 py-3">
                                <span className="flex items-center gap-1 text-orange-500">
                                  <Flame size={14} />
                                  {trip.burn_label || `Level ${trip.burn_rating}`}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <Badge className="bg-purple-100 text-purple-700">{trip.vibe}</Badge>
                              </td>
                              <td className="px-4 py-3">
                                {trip.eco_signals?.length > 0 ? (
                                  <div className="flex items-center gap-1 text-green-600">
                                    <Leaf size={14} />
                                    {trip.eco_signals.length}
                                  </div>
                                ) : '-'}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  {trip.stripe_links?.full_payment?.url && (
                                    <a href={trip.stripe_links.full_payment.url} target="_blank" rel="noopener noreferrer">
                                      <Button size="sm" variant="outline">Full</Button>
                                    </a>
                                  )}
                                  {trip.stripe_links?.deposit?.url && (
                                    <a href={trip.stripe_links.deposit.url} target="_blank" rel="noopener noreferrer">
                                      <Button size="sm" variant="outline">Deposit</Button>
                                    </a>
                                  )}
                                  {!trip.stripe_links?.full_payment?.url && !trip.stripe_links?.deposit?.url && (
                                    <span className="text-xs text-gray-400">No price</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* MEMBER CRM TAB */}
          <TabsContent value="members">
            <MemberManagement />
          </TabsContent>

          {/* BOOKINGS TAB */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-black flex items-center gap-2">
                  <CreditCard className="text-[#84bc00]" />
                  Bookings Management
                </CardTitle>
                <CardDescription>View and manage trip bookings and payments</CardDescription>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CreditCard size={48} className="mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No bookings yet</p>
                    <p className="text-sm">Bookings will appear here when customers complete purchases</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-bold">Booking ID</th>
                          <th className="px-4 py-3 text-left text-sm font-bold">Customer</th>
                          <th className="px-4 py-3 text-left text-sm font-bold">Trip</th>
                          <th className="px-4 py-3 text-left text-sm font-bold">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-bold">Travelers</th>
                          <th className="px-4 py-3 text-left text-sm font-bold">Amount</th>
                          <th className="px-4 py-3 text-left text-sm font-bold">Paid</th>
                          <th className="px-4 py-3 text-left text-sm font-bold">Balance</th>
                          <th className="px-4 py-3 text-left text-sm font-bold">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-bold">QB Sync</th>
                          <th className="px-4 py-3 text-left text-sm font-bold">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {bookings.map((booking) => (
                          <tr key={booking.booking_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-mono text-sm">{booking.booking_id}</td>
                            <td className="px-4 py-3">
                              <div className="font-medium">{booking.customer_name}</div>
                              <div className="text-xs text-gray-500">{booking.customer_email}</div>
                            </td>
                            <td className="px-4 py-3 text-sm">{booking.trip_name}</td>
                            <td className="px-4 py-3 text-sm">{booking.trip_date}</td>
                            <td className="px-4 py-3 text-sm text-center">{booking.travelers}</td>
                            <td className="px-4 py-3 text-sm font-medium">${booking.total_amount?.toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-green-600 font-medium">${booking.amount_paid?.toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-orange-600 font-medium">
                              {booking.balance_due > 0 ? `$${booking.balance_due?.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={
                                booking.status === 'paid_in_full' ? 'bg-green-100 text-green-700' :
                                booking.status === 'deposit_paid' ? 'bg-yellow-100 text-yellow-700' :
                                booking.status === 'pending' ? 'bg-gray-100 text-gray-700' :
                                'bg-red-100 text-red-700'
                              }>
                                {booking.status?.replace('_', ' ')}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              {booking.qb_invoice_id ? (
                                <CheckCircle size={16} className="text-green-500" title={`Invoice #${booking.qb_invoice_number}`} />
                              ) : (
                                <XCircle size={16} className="text-gray-300" title="Not synced" />
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                {!booking.qb_invoice_id && quickbooksStatus.connected && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={async () => {
                                      try {
                                        await axios.post(`${API_URL}/quickbooks/sync/booking/${booking.booking_id}`, {}, { withCredentials: true });
                                        toast.success('Synced to QuickBooks');
                                        loadAllData();
                                      } catch (err) {
                                        toast.error('Failed to sync');
                                      }
                                    }}
                                  >
                                    Sync to QB
                                  </Button>
                                )}
                                {booking.status !== 'refunded' && booking.status !== 'cancelled' && booking.amount_paid > 0 && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={async () => {
                                      try {
                                        const res = await axios.post(`${API_URL}/refunds/preview`, 
                                          { booking_id: booking.booking_id }, 
                                          { withCredentials: true }
                                        );
                                        setRefundPreview(res.data);
                                        setRefundReason('');
                                        setIsRefundDialogOpen(true);
                                      } catch (err) {
                                        toast.error('Failed to load refund preview');
                                      }
                                    }}
                                  >
                                    Refund
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="space-y-6">
              {/* QuickBooks Integration */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl font-black flex items-center gap-2">
                    <CreditCard className="text-[#84bc00]" />
                    QuickBooks Online Integration
                  </CardTitle>
                  <CardDescription>
                    Connect to QuickBooks to automatically sync invoices, payments, and expenses
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${quickbooksStatus.connected ? 'bg-green-100' : 'bg-gray-200'}`}>
                        {quickbooksStatus.connected ? (
                          <Link2 size={24} className="text-green-600" />
                        ) : (
                          <Unlink size={24} className="text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-lg">
                          {quickbooksStatus.connected ? 'Connected' : 'Not Connected'}
                        </p>
                        {quickbooksStatus.connected ? (
                          <p className="text-sm text-gray-500">
                            Realm ID: {quickbooksStatus.realm_id} ({quickbooksStatus.environment})
                          </p>
                        ) : (
                          <p className="text-sm text-gray-500">
                            Connect to sync bookings to QuickBooks automatically
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      {quickbooksStatus.connected ? (
                        <Button 
                          variant="outline" 
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={async () => {
                            if (window.confirm('Disconnect QuickBooks? Existing synced data will remain.')) {
                              try {
                                await axios.post(`${API_URL}/quickbooks/disconnect`, {}, { withCredentials: true });
                                toast.success('QuickBooks disconnected');
                                setQuickbooksStatus({ connected: false });
                              } catch (err) {
                                toast.error('Failed to disconnect');
                              }
                            }
                          }}
                        >
                          <Unlink size={16} className="mr-2" />
                          Disconnect
                        </Button>
                      ) : (
                        <Button 
                          className="bg-[#84bc00] hover:bg-[#6a9600]"
                          onClick={() => {
                            window.location.href = `${API_URL}/quickbooks/connect`;
                          }}
                        >
                          <Link2 size={16} className="mr-2" />
                          Connect QuickBooks
                        </Button>
                      )}
                    </div>
                  </div>

                  {quickbooksStatus.connected && (
                    <div className="mt-6 space-y-4">
                      <h4 className="font-bold">What gets synced:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 border rounded-lg">
                          <CheckCircle size={20} className="text-green-500 mb-2" />
                          <p className="font-medium">Invoices</p>
                          <p className="text-sm text-gray-500">Created for each booking</p>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <CheckCircle size={20} className="text-green-500 mb-2" />
                          <p className="font-medium">Payments</p>
                          <p className="text-sm text-gray-500">Deposits & full payments recorded</p>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <CheckCircle size={20} className="text-green-500 mb-2" />
                          <p className="font-medium">Stripe Fees</p>
                          <p className="text-sm text-gray-500">Recorded as bank charges</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Other Integrations Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl font-black">Other Integrations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <CreditCard size={20} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">Stripe Payments</p>
                          <p className="text-sm text-gray-500">Process deposits and full payments</p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-700">Connected</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Mail size={20} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">Resend (Email)</p>
                          <p className="text-sm text-gray-500">Booking confirmations & reminders</p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-700">Connected</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                          <MessageSquare size={20} className="text-pink-600" />
                        </div>
                        <div>
                          <p className="font-medium">Slack Alerts</p>
                          <p className="text-sm text-gray-500">New booking notifications</p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-700">Connected</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* EDIT DIALOG */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black text-[#84bc00]">
                {editingItem?.id || editingItem?.partner_id || editingItem?.user_id ? 'Edit' : 'Create'} {dialogType?.charAt(0).toUpperCase() + dialogType?.slice(1)}
              </DialogTitle>
            </DialogHeader>

            {editingItem && (
              <div className="space-y-4 py-4">
                {/* Common fields */}
                {(dialogType === 'package' || dialogType === 'destination' || dialogType === 'testimonial' || dialogType === 'partner') && (
                  <div>
                    <Label className="font-bold">Name</Label>
                    <Input
                      value={editingItem.name || ''}
                      onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
                      placeholder="Enter name"
                    />
                  </div>
                )}

                {(dialogType === 'package' || dialogType === 'destination' || dialogType === 'testimonial') && (
                  <div>
                    <Label className="font-bold">Description / Review</Label>
                    <Textarea
                      value={editingItem.description || editingItem.review || ''}
                      onChange={(e) => setEditingItem({
                        ...editingItem, 
                        [dialogType === 'testimonial' ? 'review' : 'description']: e.target.value
                      })}
                      rows={4}
                      placeholder="Enter description"
                    />
                  </div>
                )}

                {/* Spanish Translation Overrides */}
                {(dialogType === 'package' || dialogType === 'destination') && (
                  <div className="border-l-4 border-orange-400 pl-4 space-y-3 bg-orange-50/50 py-3 rounded-r">
                    <p className="text-xs font-bold text-orange-600 uppercase tracking-wide">🇪🇸 Spanish Translation Overrides</p>
                    <div>
                      <Label className="font-bold text-sm">Name (Spanish)</Label>
                      <Input
                        value={editingItem.name_es || ''}
                        onChange={(e) => setEditingItem({...editingItem, name_es: e.target.value})}
                        placeholder="Leave blank to use auto-translation"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="font-bold text-sm">Description (Spanish)</Label>
                      <Textarea
                        value={editingItem.description_es || ''}
                        onChange={(e) => setEditingItem({...editingItem, description_es: e.target.value})}
                        rows={3}
                        placeholder="Leave blank to use auto-translation"
                        className="text-sm"
                      />
                    </div>
                  </div>
                )}

                {(dialogType === 'package' || dialogType === 'destination' || dialogType === 'testimonial') && (
                  <div>
                    <Label className="font-bold">Image URL</Label>
                    <Input
                      value={editingItem.image || ''}
                      onChange={(e) => setEditingItem({...editingItem, image: e.target.value})}
                      placeholder="https://..."
                    />
                  </div>
                )}

                {/* Gallery Images for Package and Destination */}
                {(dialogType === 'package' || dialogType === 'destination') && (
                  <div>
                    <Label className="font-bold">Gallery Images (for carousel)</Label>
                    <p className="text-xs text-gray-500 mb-2">Add images to the carousel. Click + to add more.</p>
                    
                    {/* Individual URL inputs */}
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(editingItem.images || []).map((img, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input
                            value={img}
                            onChange={(e) => {
                              const newImages = [...(editingItem.images || [])];
                              newImages[idx] = e.target.value;
                              setEditingItem({...editingItem, images: newImages});
                            }}
                            placeholder={`Image URL ${idx + 1}`}
                            className="flex-1 text-xs"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = editingItem.images.filter((_, i) => i !== idx);
                              setEditingItem({...editingItem, images: newImages});
                            }}
                            className="bg-red-500 hover:bg-red-600 text-white rounded px-2 py-1 text-xs font-bold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Add new image button */}
                    <button
                      type="button"
                      onClick={() => {
                        const newImages = [...(editingItem.images || []), ''];
                        setEditingItem({...editingItem, images: newImages});
                      }}
                      className="mt-2 bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold py-1 px-3 rounded text-sm"
                    >
                      + Add Image URL
                    </button>
                    
                    {/* Thumbnail previews */}
                    {editingItem.images && editingItem.images.filter(img => img.trim()).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {editingItem.images.filter(img => img.trim()).map((img, idx) => (
                          <img 
                            key={idx}
                            src={img} 
                            alt={`Gallery ${idx + 1}`} 
                            className="w-12 h-12 object-cover rounded border"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/48?text=?'}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Package specific */}
                {dialogType === 'package' && (
                  <>
                    {/* New IA Fields */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="font-bold">Region *</Label>
                        <Select value={editingItem.region || ''} onValueChange={(v) => setEditingItem({...editingItem, region: v})}>
                          <SelectTrigger><SelectValue placeholder="Select region" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="north_central_america">North & Central America</SelectItem>
                            <SelectItem value="south_america">South America</SelectItem>
                            <SelectItem value="caribbean">Caribbean</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-bold">Burn Level *</Label>
                        <Select 
                          value={String(editingItem.burn_level || '')} 
                          onValueChange={(v) => setEditingItem({
                            ...editingItem, 
                            burn_level: parseInt(v),
                            burn_label: v === '1' ? 'The Socialite' : v === '2' ? 'The Vibe' : 'The Badass'
                          })}
                        >
                          <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Level 1: The Socialite</SelectItem>
                            <SelectItem value="2">Level 2: The Vibe</SelectItem>
                            <SelectItem value="3">Level 3: The Badass</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-bold">Effort (hrs/day)</Label>
                        <Input
                          value={editingItem.effort_hours || ''}
                          onChange={(e) => setEditingItem({...editingItem, effort_hours: e.target.value})}
                          placeholder="e.g., 4-6"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold">Vibes (comma-separated)</Label>
                        <Input
                          value={(editingItem.vibes || []).join(', ')}
                          onChange={(e) => setEditingItem({...editingItem, vibes: e.target.value.split(',').map(v => v.trim()).filter(Boolean)})}
                          placeholder="adventure, culture, relaxation"
                        />
                      </div>
                      <div>
                        <Label className="font-bold">Perfect For (comma-separated)</Label>
                        <Input
                          value={(editingItem.perfect_for || []).join(', ')}
                          onChange={(e) => setEditingItem({...editingItem, perfect_for: e.target.value.split(',').map(v => v.trim()).filter(Boolean)})}
                          placeholder="solo_traveler, couples, first_timer"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="font-bold">Departure Dates (comma-separated, YYYY-MM-DD)</Label>
                      <Input
                        value={(editingItem.dates || []).join(', ')}
                        onChange={(e) => setEditingItem({...editingItem, dates: e.target.value.split(',').map(v => v.trim()).filter(Boolean)})}
                        placeholder="2026-04-15, 2026-05-01, 2026-06-15"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold">Price ($)</Label>
                        <Input
                          type="number"
                          value={editingItem.price || 0}
                          onChange={(e) => setEditingItem({...editingItem, price: parseInt(e.target.value)})}
                        />
                      </div>
                      <div>
                        <Label className="font-bold">Spots Available</Label>
                        <Input
                          type="number"
                          value={editingItem.spots || 0}
                          onChange={(e) => setEditingItem({...editingItem, spots: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold">Duration</Label>
                        <Input
                          value={editingItem.duration || ''}
                          onChange={(e) => setEditingItem({...editingItem, duration: e.target.value})}
                          placeholder="e.g., 7 Days / 6 Nights"
                        />
                      </div>
                      <div>
                        <Label className="font-bold">Location</Label>
                        <Input
                          value={editingItem.location || ''}
                          onChange={(e) => setEditingItem({...editingItem, location: e.target.value})}
                          placeholder="e.g., Costa Rica"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold">Category (Legacy)</Label>
                        <Select value={editingItem.category || ''} onValueChange={(v) => setEditingItem({...editingItem, category: v})}>
                          <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Adventure">Adventure</SelectItem>
                            <SelectItem value="Beach">Beach</SelectItem>
                            <SelectItem value="Cultural">Cultural</SelectItem>
                            <SelectItem value="Wellness">Wellness</SelectItem>
                            <SelectItem value="Trekking & Heritage">Trekking & Heritage</SelectItem>
                            <SelectItem value="City & Culture">City & Culture</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-bold">Group Size</Label>
                        <Input
                          value={editingItem.groupSize || ''}
                          onChange={(e) => setEditingItem({...editingItem, groupSize: e.target.value})}
                          placeholder="e.g., Small groups (8-12)"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="font-bold">Video URL (Optional)</Label>
                      <Input
                        value={editingItem.video_url || ''}
                        onChange={(e) => setEditingItem({...editingItem, video_url: e.target.value})}
                        placeholder="YouTube or Vimeo URL"
                      />
                    </div>
                    {/* Highlights and Included */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold">Highlights (comma-separated)</Label>
                        <Textarea
                          value={(editingItem.highlights || []).join(', ')}
                          onChange={(e) => setEditingItem({...editingItem, highlights: e.target.value.split(',').map(v => v.trim()).filter(Boolean)})}
                          placeholder="Volcano hike, Surf lessons, Hot springs"
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label className="font-bold">What's Included (comma-separated)</Label>
                        <Textarea
                          value={(editingItem.included || []).join(', ')}
                          onChange={(e) => setEditingItem({...editingItem, included: e.target.value.split(',').map(v => v.trim()).filter(Boolean)})}
                          placeholder="Eco-lodges, All meals, Local guides"
                          rows={2}
                        />
                      </div>
                    </div>
                    
                    {/* Spanish overrides for highlights/included */}
                    <div className="border-l-4 border-orange-400 pl-4 space-y-3 bg-orange-50/50 py-3 rounded-r">
                      <p className="text-xs font-bold text-orange-600 uppercase tracking-wide">🇪🇸 Spanish Overrides for Lists</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-bold text-sm">Highlights (Spanish)</Label>
                          <Textarea
                            value={(editingItem.highlights_es || []).join(', ')}
                            onChange={(e) => setEditingItem({...editingItem, highlights_es: e.target.value.split(',').map(v => v.trim()).filter(Boolean)})}
                            placeholder="Senderismo volcán, Clases de surf, Aguas termales"
                            rows={2}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="font-bold text-sm">What's Included (Spanish)</Label>
                          <Textarea
                            value={(editingItem.included_es || []).join(', ')}
                            onChange={(e) => setEditingItem({...editingItem, included_es: e.target.value.split(',').map(v => v.trim()).filter(Boolean)})}
                            placeholder="Eco-lodges, Todas las comidas, Guías locales"
                            rows={2}
                            className="text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-bold text-sm">Duration (Spanish)</Label>
                          <Input
                            value={editingItem.duration_es || ''}
                            onChange={(e) => setEditingItem({...editingItem, duration_es: e.target.value})}
                            placeholder="8 días / 7 noches"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="font-bold text-sm">Group Size (Spanish)</Label>
                          <Input
                            value={editingItem.groupSize_es || ''}
                            onChange={(e) => setEditingItem({...editingItem, groupSize_es: e.target.value})}
                            placeholder="Grupos pequeños (8-12)"
                            className="text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* ECO BADGE POPUP FIELDS */}
                    <div className="border-l-4 border-green-500 pl-4 space-y-3 bg-green-50/50 py-3 rounded-r">
                      <p className="text-xs font-bold text-green-700 uppercase tracking-wide">🌿 Eco Badge Popup (hover over ECO badge)</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-bold text-sm">Energy</Label>
                          <Input
                            value={editingItem.eco_energy || ''}
                            onChange={(e) => setEditingItem({...editingItem, eco_energy: e.target.value})}
                            placeholder="100% Solar Powered Lodge"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="font-bold text-sm">Energy (Spanish)</Label>
                          <Input
                            value={editingItem.eco_energy_es || ''}
                            onChange={(e) => setEditingItem({...editingItem, eco_energy_es: e.target.value})}
                            placeholder="Lodge 100% Energía Solar"
                            className="text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-bold text-sm">Staff</Label>
                          <Input
                            value={editingItem.eco_staff || ''}
                            onChange={(e) => setEditingItem({...editingItem, eco_staff: e.target.value})}
                            placeholder="95% Local Employment"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="font-bold text-sm">Staff (Spanish)</Label>
                          <Input
                            value={editingItem.eco_staff_es || ''}
                            onChange={(e) => setEditingItem({...editingItem, eco_staff_es: e.target.value})}
                            placeholder="95% Empleo Local"
                            className="text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-bold text-sm">Waste</Label>
                          <Input
                            value={editingItem.eco_waste || ''}
                            onChange={(e) => setEditingItem({...editingItem, eco_waste: e.target.value})}
                            placeholder="Compost-only facility"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="font-bold text-sm">Waste (Spanish)</Label>
                          <Input
                            value={editingItem.eco_waste_es || ''}
                            onChange={(e) => setEditingItem({...editingItem, eco_waste_es: e.target.value})}
                            placeholder="Instalación solo compostaje"
                            className="text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* SEE THE RECEIPTS SECTION */}
                    <div className="border-l-4 border-gray-700 pl-4 space-y-3 bg-gray-100 py-3 rounded-r">
                      <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">🧾 See The Receipts Section</p>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-bold text-sm">Intro Text</Label>
                          <Textarea
                            value={editingItem.receipts_intro || ''}
                            onChange={(e) => setEditingItem({...editingItem, receipts_intro: e.target.value})}
                            placeholder="We don't just hike; we protect the trails we walk on."
                            rows={2}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="font-bold text-sm">Intro Text (Spanish)</Label>
                          <Textarea
                            value={editingItem.receipts_intro_es || ''}
                            onChange={(e) => setEditingItem({...editingItem, receipts_intro_es: e.target.value})}
                            placeholder="No solo hacemos senderismo; protegemos los senderos que recorremos."
                            rows={2}
                            className="text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="font-bold text-sm">Receipt Items (one per line)</Label>
                        <Textarea
                          value={(editingItem.receipts_items || []).join('\n')}
                          onChange={(e) => setEditingItem({...editingItem, receipts_items: e.target.value.split('\n').filter(Boolean)})}
                          placeholder="100% Indigenous-Led: Your guides are community members...&#10;Human-Powered Only: No vehicles on the trail...&#10;$50 from every booking goes to reforestation..."
                          rows={5}
                          className="text-sm"
                        />
                      </div>

                      <div>
                        <Label className="font-bold text-sm">Receipt Items (Spanish, one per line)</Label>
                        <Textarea
                          value={(editingItem.receipts_items_es || []).join('\n')}
                          onChange={(e) => setEditingItem({...editingItem, receipts_items_es: e.target.value.split('\n').filter(Boolean)})}
                          placeholder="100% Liderado por Indígenas: Tus guías son miembros de la comunidad..."
                          rows={5}
                          className="text-sm"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-bold text-sm">Verified By (one per line)</Label>
                          <Textarea
                            value={(editingItem.receipts_verified_by || []).join('\n')}
                            onChange={(e) => setEditingItem({...editingItem, receipts_verified_by: e.target.value.split('\n').filter(Boolean)})}
                            placeholder="Pachamama Alliance (Community Audit)&#10;Leave No Trace (Gold Partner)&#10;Exciting.Travel On-Site Audit (May 2025)"
                            rows={3}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="font-bold text-sm">Verified By (Spanish)</Label>
                          <Textarea
                            value={(editingItem.receipts_verified_by_es || []).join('\n')}
                            onChange={(e) => setEditingItem({...editingItem, receipts_verified_by_es: e.target.value.split('\n').filter(Boolean)})}
                            placeholder="Alianza Pachamama (Auditoría Comunitaria)&#10;Leave No Trace (Socio Oro)..."
                            rows={3}
                            className="text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={editingItem.featured || false}
                          onCheckedChange={(v) => setEditingItem({...editingItem, featured: v})}
                        />
                        <Label>Featured</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={editingItem.sustainable !== false}
                          onCheckedChange={(v) => setEditingItem({...editingItem, sustainable: v})}
                        />
                        <Label>Eco-Certified</Label>
                      </div>
                    </div>
                  </>
                )}

                {/* Destination specific */}
                {dialogType === 'destination' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold">Region *</Label>
                        <Select value={editingItem.region || ''} onValueChange={(v) => setEditingItem({...editingItem, region: v})}>
                          <SelectTrigger><SelectValue placeholder="Select region" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="north_central_america">North & Central America</SelectItem>
                            <SelectItem value="south_america">South America</SelectItem>
                            <SelectItem value="caribbean">Caribbean</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-bold">Country</Label>
                        <Input
                          value={editingItem.country || ''}
                          onChange={(e) => setEditingItem({...editingItem, country: e.target.value})}
                          placeholder="e.g., Costa Rica"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold">Vibes (comma-separated)</Label>
                        <Input
                          value={(editingItem.vibes || []).join(', ')}
                          onChange={(e) => setEditingItem({...editingItem, vibes: e.target.value.split(',').map(v => v.trim()).filter(Boolean)})}
                          placeholder="adventure, culture, relaxation"
                        />
                      </div>
                      <div>
                        <Label className="font-bold">Activities (comma-separated)</Label>
                        <Input
                          value={(editingItem.activities || []).join(', ')}
                          onChange={(e) => setEditingItem({...editingItem, activities: e.target.value.split(',').map(v => v.trim()).filter(Boolean)})}
                          placeholder="surfing, hiking, wildlife"
                        />
                      </div>
                    </div>
                    {/* Spanish Activities Override */}
                    <div className="border-l-4 border-orange-400 pl-4 bg-orange-50/50 py-2 rounded-r">
                      <Label className="font-bold text-sm">🇪🇸 Activities (Spanish Override)</Label>
                      <Input
                        value={(editingItem.activities_es || []).join(', ')}
                        onChange={(e) => setEditingItem({...editingItem, activities_es: e.target.value.split(',').map(v => v.trim()).filter(Boolean)})}
                        placeholder="buceo, senderismo, vida silvestre"
                        className="text-sm mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label className="font-bold">Currency</Label>
                        <Input
                          value={editingItem.logistics?.currency || ''}
                          onChange={(e) => setEditingItem({...editingItem, logistics: {...(editingItem.logistics || {}), currency: e.target.value}})}
                          placeholder="USD"
                        />
                      </div>
                      <div>
                        <Label className="font-bold">Language</Label>
                        <Input
                          value={editingItem.logistics?.language || ''}
                          onChange={(e) => setEditingItem({...editingItem, logistics: {...(editingItem.logistics || {}), language: e.target.value}})}
                          placeholder="Spanish"
                        />
                      </div>
                      <div>
                        <Label className="font-bold">Timezone</Label>
                        <Input
                          value={editingItem.logistics?.timezone || ''}
                          onChange={(e) => setEditingItem({...editingItem, logistics: {...(editingItem.logistics || {}), timezone: e.target.value}})}
                          placeholder="CST"
                        />
                      </div>
                      <div>
                        <Label className="font-bold">Best Time</Label>
                        <Input
                          value={editingItem.logistics?.best_time || ''}
                          onChange={(e) => setEditingItem({...editingItem, logistics: {...(editingItem.logistics || {}), best_time: e.target.value}})}
                          placeholder="Nov-Apr"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold">Location (Legacy)</Label>
                        <Input
                          value={editingItem.location || ''}
                          onChange={(e) => setEditingItem({...editingItem, location: e.target.value})}
                          placeholder="e.g., Costa Rica"
                        />
                      </div>
                      <div>
                        <Label className="font-bold">Category (Legacy)</Label>
                        <Select value={editingItem.category || ''} onValueChange={(v) => setEditingItem({...editingItem, category: v})}>
                          <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Beach & Nightlife">Beach & Nightlife</SelectItem>
                            <SelectItem value="Culture & Food">Culture & Food</SelectItem>
                            <SelectItem value="Adventure & Wellness">Adventure & Wellness</SelectItem>
                            <SelectItem value="Music & Beach">Music & Beach</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}

                {/* Testimonial specific */}
                {dialogType === 'testimonial' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold">Trip</Label>
                        <Input
                          value={editingItem.trip || ''}
                          onChange={(e) => setEditingItem({...editingItem, trip: e.target.value})}
                          placeholder="e.g., Costa Rica Adventure"
                        />
                      </div>
                      <div>
                        <Label className="font-bold">Rating</Label>
                        <Select value={String(editingItem.rating || 5)} onValueChange={(v) => setEditingItem({...editingItem, rating: parseInt(v)})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map(r => (
                              <SelectItem key={r} value={String(r)}>{r} Star{r > 1 ? 's' : ''}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={editingItem.featured || false}
                        onCheckedChange={(v) => setEditingItem({...editingItem, featured: v})}
                      />
                      <Label>Featured on Homepage</Label>
                    </div>
                  </>
                )}

                {/* Partner specific */}
                {dialogType === 'partner' && (
                  <>
                    <div>
                      <Label className="font-bold">Logo URL</Label>
                      <Input
                        value={editingItem.logo || ''}
                        onChange={(e) => setEditingItem({...editingItem, logo: e.target.value})}
                        placeholder="https://..."
                      />
                    </div>
                    <div>
                      <Label className="font-bold">Description</Label>
                      <Textarea
                        value={editingItem.description || ''}
                        onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label className="font-bold">Affiliate Link</Label>
                      <Input
                        value={editingItem.affiliateLink || ''}
                        onChange={(e) => setEditingItem({...editingItem, affiliateLink: e.target.value})}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold">Category</Label>
                        <Select value={editingItem.category || ''} onValueChange={(v) => setEditingItem({...editingItem, category: v})}>
                          <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Booking Platform">Booking Platform</SelectItem>
                            <SelectItem value="Tour Operator">Tour Operator</SelectItem>
                            <SelectItem value="Activities">Activities</SelectItem>
                            <SelectItem value="Accommodations">Accommodations</SelectItem>
                            <SelectItem value="Cultural Tours">Cultural Tours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-bold">Display Order</Label>
                        <Input
                          type="number"
                          value={editingItem.display_order || 0}
                          onChange={(e) => setEditingItem({...editingItem, display_order: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={editingItem.active !== false}
                        onCheckedChange={(v) => setEditingItem({...editingItem, active: v})}
                      />
                      <Label>Active</Label>
                    </div>
                  </>
                )}

                {/* User specific */}
                {dialogType === 'user' && (
                  <>
                    <div>
                      <Label className="font-bold">Name</Label>
                      <Input
                        value={editingItem.name || ''}
                        onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="font-bold">Email</Label>
                      <Input
                        type="email"
                        value={editingItem.email || ''}
                        onChange={(e) => setEditingItem({...editingItem, email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="font-bold">Role</Label>
                      <Select 
                        value={editingItem.role || 'user'} 
                        onValueChange={(value) => setEditingItem({...editingItem, role: value})}
                      >
                        <SelectTrigger data-testid="user-role-select">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        Admin users can access the admin dashboard to manage content.
                      </p>
                    </div>
                  </>
                )}

                {/* Actions */}
                <div className="flex space-x-4 pt-4 border-t">
                  <Button onClick={handleSave} className="bg-[#84bc00] hover:bg-[#6a9600]">
                    <Save size={18} className="mr-2" />SAVE
                  </Button>
                  <Button onClick={() => setIsDialogOpen(false)} variant="outline">
                    <X size={18} className="mr-2" />CANCEL
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* REFUND DIALOG */}
        <Dialog open={isRefundDialogOpen} onOpenChange={setIsRefundDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black text-red-600 flex items-center gap-2">
                <AlertCircle size={24} />
                Process Refund
              </DialogTitle>
              <DialogDescription>
                Review the refund details based on your cancellation policy
              </DialogDescription>
            </DialogHeader>

            {refundPreview && (
              <div className="space-y-4 py-4">
                {/* Booking Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-bold">{refundPreview.customer_name}</p>
                  <p className="text-sm text-gray-500">{refundPreview.customer_email}</p>
                  <p className="text-sm mt-2">{refundPreview.trip_name}</p>
                  <p className="text-sm text-gray-500">Trip Date: {refundPreview.trip_date}</p>
                </div>

                {/* Policy Applied */}
                <div className={`p-4 rounded-lg border-2 ${
                  refundPreview.policy_tier === 'full_refund' ? 'bg-green-50 border-green-200' :
                  refundPreview.policy_tier === 'partial_refund' ? 'bg-yellow-50 border-yellow-200' :
                  'bg-red-50 border-red-200'
                }`}>
                  <p className="font-bold text-sm">
                    {refundPreview.days_until_trip} days until trip
                  </p>
                  <p className="text-sm mt-1">{refundPreview.policy_description}</p>
                </div>

                {/* Amounts */}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Paid</span>
                    <span className="font-medium">${refundPreview.amount_paid?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Refund Percentage</span>
                    <span className="font-medium">{refundPreview.refund_percentage}%</span>
                  </div>
                  <hr />
                  <div className="flex justify-between text-lg">
                    <span className="font-bold text-green-600">Refund Amount</span>
                    <span className="font-black text-green-600">${refundPreview.refund_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Retained Amount</span>
                    <span className="text-gray-500">${refundPreview.retained_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <Label className="font-bold">Cancellation Reason</Label>
                  <Textarea
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="Customer requested cancellation..."
                    className="mt-1"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsRefundDialogOpen(false)}
                    disabled={processingRefund}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    disabled={processingRefund}
                    onClick={async () => {
                      setProcessingRefund(true);
                      try {
                        const res = await axios.post(`${API_URL}/refunds/process`, {
                          booking_id: refundPreview.booking_id,
                          reason: refundReason || 'Customer requested cancellation'
                        }, { withCredentials: true });
                        
                        if (res.data.success) {
                          toast.success(`Refund of $${res.data.refund_amount?.toLocaleString()} processed successfully`);
                          setIsRefundDialogOpen(false);
                          loadAllData();
                        } else {
                          toast.error('Refund failed');
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to process refund');
                      } finally {
                        setProcessingRefund(false);
                      }
                    }}
                  >
                    {processingRefund ? 'Processing...' : `Process Refund ($${refundPreview.refund_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})})`}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPanel;
